import config from '@/config/env';

/**
 * Client API cơ bản để gọi các API endpoints
 */
class ApiClient {
  private baseUrl: string;
  private defaultHeaders: Record<string, string>;

  constructor(baseUrl: string = config.apiUrl) {
    this.baseUrl = baseUrl;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
  }

  /**
   * Gửi request đến API
   */
  async request<T>(
    endpoint: string,
    method: string = 'GET',
    data?: any,
    customHeaders?: Record<string, string>
  ): Promise<T> {
    const url = this.resolveUrl(endpoint);
    const headers = { ...this.defaultHeaders, ...customHeaders };
    
    const options: RequestInit = {
      method,
      headers,
      credentials: 'include', // Bao gồm cookie nếu có
    };

    if (data && method !== 'GET') {
      options.body = JSON.stringify(data);
    }

    try {
      const response = await fetch(url, options);
      
      // Handle HTTP error status
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error ${response.status}`);
      }
      
      // Handle empty response (204 No Content)
      if (response.status === 204) {
        return {} as T;
      }
      
      // Parse JSON response
      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, 'GET', undefined, headers);
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data: any, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, 'POST', data, headers);
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data: any, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, 'PUT', data, headers);
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, data: any, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, 'PATCH', data, headers);
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, 'DELETE', undefined, headers);
  }

  /**
   * Tạo URL đầy đủ từ endpoint
   */
  private resolveUrl(endpoint: string): string {
    // Nếu endpoint đã là URL đầy đủ, trả về nguyên vẹn
    if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
      return endpoint;
    }
    
    // Nếu không, thêm baseUrl vào trước
    return `${this.baseUrl}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
  }

  /**
   * Thiết lập token xác thực
   */
  setAuthToken(token: string) {
    this.defaultHeaders = {
      ...this.defaultHeaders,
      Authorization: `Bearer ${token}`,
    };
  }

  /**
   * Xóa token xác thực
   */
  clearAuthToken() {
    const headers = { ...this.defaultHeaders };
    if (headers.Authorization) {
      delete headers.Authorization;
    }
    this.defaultHeaders = headers;
  }
}

// Tạo instance mặc định
const apiClient = new ApiClient();

export default apiClient; 